import { useCopy } from '@/i18n/useCopy'
import type { Profile, Slot, SlotId } from '@/profile/types'
import { needsConfirmation } from '@/profile/types'
import styles from './Ledger.module.css'

interface LedgerProps {
  profile: Profile
  /** Called when a chip is tapped, to correct that answer. */
  onEdit?(slot: SlotId): void
  showHint?: boolean
}

/**
 * The answer ledger.
 *
 * This is the form, replaced. As the conversation captures each answer, it
 * appears here, so the person can watch their profile being written by talking
 * rather than by filling anything in. Two things it buys:
 *
 *  - Trust. Nothing is recorded invisibly. Everything Namu believes about you
 *    is on screen while it is being decided.
 *  - Correction. Every chip is one tap from being changed, which is the
 *    escape hatch for the answers a recogniser gets wrong, and recognisers get
 *    names and places wrong far more often than ordinary words.
 */
export function Ledger({ profile, onEdit, showHint = true }: LedgerProps) {
  const copy = useCopy()

  const entries: Array<{ id: SlotId; label: string; value: string; slot: Slot<unknown> | null }> = [
    {
      id: 'name',
      label: copy.ui.field.name,
      value: profile.name?.value ?? '',
      slot: profile.name,
    },
    {
      id: 'region',
      label: copy.ui.field.region,
      value: profile.region?.value ?? '',
      slot: profile.region,
    },
    {
      id: 'ageBand',
      label: copy.ui.field.ageBand,
      value: profile.ageBand ? copy.ui.ageBand[profile.ageBand.value] : '',
      slot: profile.ageBand,
    },
    {
      id: 'gender',
      label: copy.ui.field.gender,
      value: profile.gender ? copy.ui.gender[profile.gender.value] : '',
      slot: profile.gender,
    },
    {
      id: 'interests',
      label: copy.ui.field.interests,
      value: profile.interests?.value.join(', ') ?? '',
      slot: profile.interests,
    },
  ]

  const captured = entries.filter((entry) => entry.slot !== null)

  return (
    <section className={styles.ledger} data-testid="ledger" aria-label={copy.ui.ledger.title}>
      <h2 className={styles.title}>{copy.ui.ledger.title}</h2>

      {captured.length === 0 ? (
        <p className={styles.empty}>{copy.ui.ledger.empty}</p>
      ) : (
        <ul className={styles.list} role="list">
          {captured.map((entry) => {
            const uncertain = needsConfirmation(entry.slot)
            const chipContent = (
              <>
                <span className={styles.chipLabel}>{entry.label}</span>
                <span className={styles.chipValue}>{entry.value}</span>
              </>
            )

            return (
              <li key={entry.id}>
                {onEdit ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${uncertain ? styles.uncertain : ''}`}
                    onClick={() => onEdit(entry.id)}
                    data-testid={`ledger-item-${entry.id}`}
                    aria-label={`${copy.ui.ledger.correct}: ${entry.label}, ${entry.value}`}
                  >
                    {chipContent}
                  </button>
                ) : (
                  <span
                    className={`${styles.chip} ${uncertain ? styles.uncertain : ''}`}
                    data-testid={`ledger-item-${entry.id}`}
                  >
                    {chipContent}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {showHint && captured.length > 0 && onEdit ? (
        <p className={styles.hint}>{copy.ui.ledger.editHint}</p>
      ) : null}
    </section>
  )
}
